import EntryActions from 'actions/EntryActions';
import BenchlingConfig from 'BenchlingConfig';
import EntryDAO from 'dao/EntryDAO';
import {EntryWithFulfillments} from 'notebook/ELNConstants';
import TemplateActions from 'notebook/entry-templates/TemplateActions';
import {FromServerTemplate} from 'notebook/entry-templates/TemplateConstants';
import TemplateDAO from 'notebook/entry-templates/TemplateDAO';
import TemplateStore from 'notebook/entry-templates/TemplateStore';
import {RealtimeMessageType} from 'realtime/Constants';
import {GetRealtimeMessageData} from 'realtime/RealtimeChannel';
import {isCurrentClient} from 'realtime/util/RealtimeUtil';
import EntryStore from 'stores/EntryStore';
import APIUtil from 'util/APIUtil';

import type {DocumentUpdateMessageHandler} from './DocumentUpdateMessageHandlerConstants';

type BaseEntryStoreActionsAndDAO =
  | [typeof EntryStore, typeof EntryActions, typeof EntryDAO]
  | [typeof TemplateStore, typeof TemplateActions, typeof TemplateDAO];

function getBaseEntryStoreActionsAndDAO(fileId: string): BaseEntryStoreActionsAndDAO | null {
  if (APIUtil.isEntry(fileId)) {
    return [EntryStore, EntryActions, EntryDAO];
  } else if (APIUtil.isEntryTemplate(fileId) && BenchlingConfig.ELN_REALTIME_TEMPLATE_UPDATES) {
    return [TemplateStore, TemplateActions, TemplateDAO];
  }
  return null;
}

export const reviewUpdateMessageHandler: DocumentUpdateMessageHandler<
  typeof RealtimeMessageType.REVIEW_UPDATE
> = {
  active: (
    fileId: string,
    {clientId}: GetRealtimeMessageData<typeof RealtimeMessageType.REVIEW_UPDATE>
  ): void => {
    if (!clientId || isCurrentClient(clientId)) {
      return;
    }

    const storeActionsAndDAO = getBaseEntryStoreActionsAndDAO(fileId);
    if (storeActionsAndDAO == null) {
      return;
    }
    const [Store, Actions, DAO] = storeActionsAndDAO;
    if (Store.inStore(fileId)) {
      Promise.resolve(DAO.get(fileId))
        .then((response: EntryWithFulfillments | FromServerTemplate) => {
          if (Store.inStore(fileId)) {
            const {permissions} = response;
            const review = response.review ?? undefined;
            Actions.postServerUpdate(fileId, {review, permissions});
          }
        })
        .catch(() => {});
    }
  },
  inactive: (
    fileId: string,
    {clientId}: GetRealtimeMessageData<typeof RealtimeMessageType.REVIEW_UPDATE>
  ): void => {
    if (!clientId || isCurrentClient(clientId)) {
      return;
    }

    const storeActionsAndDAO = getBaseEntryStoreActionsAndDAO(fileId);
    if (storeActionsAndDAO == null) {
      return;
    }
    const [Store, Actions] = storeActionsAndDAO;
    if (Store.inStore(fileId)) {
      Actions.postServerUpdate(fileId, {isStale: true});
    }
  },
};
